/**
 * IAM User Pool List Component
 * First level: Lists all user pools across environments
 */

import React, { useState, useEffect } from 'react';
import { UniversalSchemaTable } from '../shared/UniversalSchemaTable';
import { IAM_M2M_OPENAPI } from '../../schemas';

interface UserPool {
  id: string;
  name: string;
  environment: string;
  environmentType: 'production' | 'nonproduction';
  status?: string;
  creationDate?: string;
  lastModifiedDate?: string;
}

interface IamUserPoolListProps {
  onSelectUserPool: (userPoolId: string, userPoolName: string, userPoolEnvironment?: string) => void;
}

const IamUserPoolList: React.FC<IamUserPoolListProps> = ({ onSelectUserPool }) => {
  const [userPools, setUserPools] = useState<UserPool[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserPools();
  }, []);

  const loadUserPools = async () => {
    try {
      setLoading(true);
      
      // Import ApiService dynamically to avoid circular imports
      const { ApiService } = await import('../../services/api');
      
      // Call the real IAM M2M API
      const response = await ApiService.listIamUserPools();
      setUserPools(response.userPools || []);
    } catch (err) {
      console.error('Error loading user pools:', err);
      // Don't fallback to mock data - let the error surface so we can fix it
      setUserPools([]);
      throw err; // Re-throw to surface the error
    } finally {
      setLoading(false);
    }
  };

  const handleManageUserPool = (userPool: UserPool) => {
    onSelectUserPool(userPool.id, userPool.name, userPool.environment);
  };

  const customActions = [
    {
      label: 'Manage Pool',
      icon: 'fas fa-cogs',
      variant: 'primary' as const,
      onClick: handleManageUserPool
    }
  ];

  const customLabels = {
    id: 'User Pool ID',
    name: 'Name',
    environment: 'Environment',
    environmentType: 'Type',
    status: 'Status',
    creationDate: 'Created',
    lastModifiedDate: 'Last Modified'
  };

  const customColumnTypes = {
    environmentType: 'badge' as const,
    status: 'badge' as const,
    creationDate: 'datetime' as const,
    lastModifiedDate: 'datetime' as const
  };

  if (loading) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="fas fa-spinner fa-spin fa-3x text-primary mb-3"></i>
              <h4>Loading User Pools...</h4>
              <p className="text-muted">Fetching user pools from all environments</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">
              <i className="fas fa-database mr-2"></i>
              User Pools
            </h3>
            <div className="card-tools">
              <button
                type="button"
                className="btn btn-tool"
                onClick={loadUserPools}
                title="Refresh"
              >
                <i className="fas fa-sync-alt"></i>
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-12">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle mr-2"></i>
                  <strong>Cross-Account Access:</strong> This system manages user pools across multiple AWS accounts.
                  <br />
                  <small>
                    • <strong>Non-Production:</strong> 4 user pools in development account<br />
                    • <strong>Production:</strong> 1 user pool in production account
                  </small>
                </div>
              </div>
            </div>

            <UniversalSchemaTable
              openApiSpec={IAM_M2M_OPENAPI}
              schemaName="UserPool"
              data={userPools}
              loading={loading}
              customActions={customActions}
              customLabels={customLabels}
              customColumnTypes={customColumnTypes}
              emptyMessage="No user pools found"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default IamUserPoolList;